import { useState } from 'react';
import { REGION_LIST } from '@constants/limits';
import { LOCATION_FILTERS as T } from '@locales/id/locations';
import { Icon, MenuChoice } from '@ui';
import { useDistricts, useVillages, type AreaListState } from '@features/locations/model/useAreaLists';

type AreaList = AreaListState<{ code: string; name: string }>;

/** One server-backed radio list: "Semua …" first, the page narrowed by the typed words, its loading / error / empty. */
function AreaList({
  heading,
  all,
  search,
  none,
  value,
  q,
  setQ,
  list,
  onPick,
}: {
  heading: string;
  all: string;
  search: string;
  none: string;
  value: string;
  q: string;
  setQ: (q: string) => void;
  list: AreaList;
  onPick: (code: string) => void;
}) {
  const { state, items, partial } = list;
  const typed = q.trim();
  const firstLoad = state.loading && !state.data;
  return (
    <>
      <p className="fmenu-heading">{heading}</p>
      <label className="fmenu-search">
        <Icon name="search" size={16} />
        <span className="sr-only">{search}</span>
        <input type="search" value={q} placeholder={search} onChange={(e) => setQ(e.target.value)} data-menu-item autoComplete="off" />
      </label>
      <div className="fmenu-list fmenu-scroll loc-region-list" role="radiogroup" aria-label={heading} aria-busy={state.loading || undefined}>
        {!typed && (
          <MenuChoice checked={!value} onSelect={() => onPick('')}>
            {all}
          </MenuChoice>
        )}
        {items.map((x, i) => (
          <MenuChoice key={x.code} checked={value === x.code} focusable={!!typed && i === 0} onSelect={() => onPick(x.code)}>
            {x.name}
          </MenuChoice>
        ))}
      </div>
      {firstLoad ? (
        <p className="fmenu-state" role="status">
          {T.areaLoading}
        </p>
      ) : state.error && !state.data ? (
        <p className="fmenu-state" role="alert">
          {T.areaFailed}{' '}
          <button type="button" className="loc-link-btn" data-menu-item onClick={state.retry}>
            {T.retry}
          </button>
        </p>
      ) : state.data && !items.length ? (
        <p className="fmenu-state" role="status">
          {/* a cut list: what is typed may be past the first page, the server searches from searchMin letters */}
          {partial ? T.areaTypeMore(REGION_LIST.searchMin) : typed ? none : T.areaNone}
        </p>
      ) : (
        partial && (
          <p className="fmenu-state" role="status">
            {T.areaTypeMore(REGION_LIST.searchMin)}
          </p>
        )
      )}
    </>
  );
}

/** A search typed for one parent: another parent starts empty. */
function useScopedQuery(parent: string) {
  const [typed, setTyped] = useState({ parent, q: '' });
  return [typed.parent === parent ? typed.q : '', (q: string) => setTyped({ parent, q })] as const;
}

/**
 * Kecamatan, then desa/kelurahan, under a chosen regency in a filter menu: each a page of its parent (never the ±84
 * thousand villages), searchable. Choosing a district keeps the menu open for its villages; choosing a village closes
 * it (`onVillage`'s caller). Nothing is fetched until `regencyCode` is set and the menu is open (it renders).
 */
export function AreaPanel({
  regencyCode,
  districtCode,
  villageCode,
  onDistrict,
  onVillage,
}: {
  regencyCode: string;
  districtCode: string;
  villageCode: string;
  onDistrict: (code: string) => void;
  onVillage: (code: string) => void;
}) {
  const [dq, setDq] = useScopedQuery(regencyCode);
  const [vq, setVq] = useScopedQuery(districtCode);
  const districts = useDistricts(regencyCode, dq);
  const villages = useVillages(districtCode, vq);
  if (!regencyCode) return null;
  return (
    <>
      <hr className="fmenu-sep" />
      <AreaList
        key={`d:${regencyCode}`}
        heading={T.district}
        all={T.allDistricts}
        search={T.districtSearch}
        none={T.noDistrict}
        value={districtCode}
        q={dq}
        setQ={setDq}
        list={districts}
        onPick={onDistrict}
      />
      {districtCode && (
        <>
          <hr className="fmenu-sep" />
          <AreaList
            key={`v:${districtCode}`}
            heading={T.village}
            all={T.allVillages}
            search={T.villageSearch}
            none={T.noVillage}
            value={villageCode}
            q={vq}
            setQ={setVq}
            list={villages}
            onPick={onVillage}
          />
        </>
      )}
    </>
  );
}
